import Disclosure from "./Disclosure";
import {
  controlClass,
  describedBy,
  FieldError,
  HelpText,
  isRequiredNow,
  labelClass,
  RequiredMark,
  wrapperProps,
  type FieldProps,
} from "./shared";

/**
 * The port of fragments/forms/fields/text.php: a single-line field. Covers the
 * "text", "email" and "tel" field types: the same chrome, only the input type
 * differs.
 *
 * A field that is only required when another field holds a particular value is
 * also a field that is only worth showing then, so a "required_if" field goes
 * out hidden and the form shows it once the rule is met. Hidden here means the
 * hidden attribute, so a field nobody is being asked to fill in is not read out
 * or tabbed into either.
 *
 * The theme ran the label through wp_kses_post() so it could carry a link; the
 * labels here are plain text.
 */
export default function TextField(props: FieldProps) {
  const {
    id,
    name,
    field,
    autocomplete,
    value,
    error,
    shown,
    onChange,
    onFocus,
  } = props;

  if (!id || !name || !field) {
    return null;
  }

  const type =
    field.type === "email" || field.type === "tel" ? field.type : "text";
  const required = isRequiredNow(field, shown);
  const maxLength =
    field.max_length || field.validation?.max_length || undefined;

  return (
    <div {...wrapperProps(props)}>
      <label className={labelClass} htmlFor={id}>
        {field.label} <RequiredMark field={field} />
      </label>

      <HelpText id={id} field={field} />

      <input
        className={`${controlClass} js-field-el`}
        type={type}
        id={id}
        name={name}
        value={value}
        placeholder={field.placeholder || undefined}
        autoComplete={autocomplete || undefined}
        maxLength={maxLength}
        readOnly={field.readonly || undefined}
        required={required}
        aria-required={required || undefined}
        aria-invalid={!!error}
        aria-describedby={describedBy(id, field)}
        data-validate="1"
        data-required={required ? "1" : undefined}
        onChange={(event) => onChange(name, event.target.value)}
        onFocus={() => onFocus(name)}
      />

      <FieldError id={id} error={error} />

      <Disclosure id={id} disclosure={field.disclosure} />
    </div>
  );
}
